import type {
  CelulaKanbanEntity,
  FamiliaCor,
  LimiteWip,
  PortePeca,
  TipoTinta,
  UnidadeWip,
} from '@pieltec/contracts';

export const UNIDADES_WIP: readonly { value: UnidadeWip; label: string }[] = [
  { value: 'CARTOES', label: 'Cartões' },
  { value: 'PECAS', label: 'Peças' },
  { value: 'KG', label: 'kg' },
  { value: 'M2', label: 'm²' },
  { value: 'GANCHEIRAS', label: 'Gancheiras' },
];

export function validarLimitesWip(limites: readonly LimiteWip[]): string | null {
  const set = new Set<string>();
  for (const l of limites) {
    if (!(l.limite > 0)) return 'Limite de WIP deve ser maior que zero.';
    if (!Number.isInteger(l.limite)) return 'Limite de WIP deve ser um número inteiro.';
    if (set.has(l.unidade)) return 'Unidade repetida não é permitida.';
    set.add(l.unidade);
  }
  return null;
}

/** Linha do editor de limites: o limite fica como texto do input ate a validacao. */
export interface LinhaLimiteWip {
  unidade: UnidadeWip;
  limite: string;
}

export interface ErroLimiteWip {
  indice: number;
  mensagem: string;
}

const ORDEM_UNIDADES: readonly string[] = UNIDADES_WIP.map((u) => u.value);

/** Ordem de exibicao: Cartoes, Pecas, kg, m2, Gancheiras (D-26). */
export function ordenarLimites<T extends { unidade: UnidadeWip }>(limites: readonly T[]): T[] {
  return [...limites].sort((a, b) => ORDEM_UNIDADES.indexOf(a.unidade) - ORDEM_UNIDADES.indexOf(b.unidade));
}

export function paraLinhas(limites: readonly LimiteWip[]): LinhaLimiteWip[] {
  return ordenarLimites(limites).map((l) => ({ unidade: l.unidade, limite: String(l.limite) }));
}

export function deLinhas(linhas: readonly LinhaLimiteWip[]): LimiteWip[] {
  return linhas.map((l) => ({ unidade: l.unidade, limite: Number(l.limite) }));
}

/** Primeira unidade (na ordem canonica) que ainda nao tem linha; null com as 5 em uso. */
export function proximaUnidadeLivre(linhas: readonly { unidade: UnidadeWip }[]): UnidadeWip | null {
  const usadas = new Set(linhas.map((l) => l.unidade));
  return UNIDADES_WIP.find((u) => !usadas.has(u.value))?.value ?? null;
}

/** Primeiro erro das linhas, com o indice da linha para a mensagem ficar junto do campo. */
export function validarLinhasLimiteWip(linhas: readonly LinhaLimiteWip[]): ErroLimiteWip | null {
  const usadas = new Set<string>();
  for (const [indice, linha] of linhas.entries()) {
    const texto = linha.limite.trim();
    const numero = texto === '' ? Number.NaN : Number(texto);
    if (!(numero > 0)) return { indice, mensagem: 'Limite de WIP deve ser maior que zero.' };
    if (!Number.isInteger(numero)) return { indice, mensagem: 'Limite de WIP deve ser um número inteiro.' };
    if (usadas.has(linha.unidade)) return { indice, mensagem: 'Unidade repetida não é permitida.' };
    usadas.add(linha.unidade);
  }
  return null;
}

/**
 * WR-GAP-01: os formularios Kanban gravam em varias requisicoes (o PUT de limites substitui
 * tudo). So se pode gravar com a carga inicial completa e sem gravacao em andamento; do
 * contrario o formulario mostraria (e enviaria) dados vazios no lugar dos gravados.
 */
export function podeGravarFormulario(estado: { loading: boolean; cargaOk: boolean; saving: boolean }): boolean {
  return !estado.loading && estado.cargaOk && !estado.saving;
}

/** Compara conjuntos de limites sem depender da ordem. */
export function limitesIguais(a: readonly LimiteWip[], b: readonly LimiteWip[]): boolean {
  if (a.length !== b.length) return false;
  const ordenadoA = ordenarLimites(a);
  const ordenadoB = ordenarLimites(b);
  return ordenadoA.every((l, i) => l.unidade === ordenadoB[i]?.unidade && l.limite === ordenadoB[i]?.limite);
}

export function validarCelulaKanban(form: { nome: string }): string | null {
  if (!form.nome || form.nome.trim() === '') {
    return 'Informe o nome da célula.';
  }
  return null;
}

export function validarEstufa(form: { cabines: string[] }): string | null {
  if (!form.cabines || form.cabines.length === 0) {
    return 'Selecione ao menos uma cabine para a estufa.';
  }
  return null;
}

/**
 * WR-GAP-05: a lista de servicos so traz ativos. Se o servico vinculado a celula foi
 * inativado depois, ele nao esta na lista e o select mostraria "Sem processo" enquanto o
 * estado guarda o id real; nesse caso o formulario acrescenta o atual como opcao inativa.
 */
export function servicoAtualForaDaLista(
  servicoId: string | null | undefined,
  servicos: readonly { id: string }[],
): boolean {
  return Boolean(servicoId) && !servicos.some((s) => s.id === servicoId);
}

/**
 * WR-GAP-03: dados legados podem ter celulas nao-cabine com estufaId. O formulario so lista
 * cabines, entao esses codigos ficariam marcados sem checkbox para desmarcar e a API
 * recusaria o salvamento (400). Mantem so os codigos que aparecem na lista; ao salvar, a API
 * desvincula o restante.
 */
export function manterCabinesVisiveis(marcadas: readonly string[], visiveis: readonly string[]): string[] {
  const lista = new Set(visiveis);
  return marcadas.filter((codigo) => lista.has(codigo));
}

export interface ErroFormularioEstufa {
  campo: 'codigo' | 'nome' | 'cabines' | 'limites';
  mensagem: string;
  /** Linha do editor de limites (só em campo 'limites'). */
  indice?: number;
}

/**
 * Primeiro erro do formulario de estufa, na ordem dos campos: codigo (so na criacao), nome,
 * cabines e limites. Estufa inativa nao tem cabines (a API recusa com 409 do contrario).
 */
export function validarFormularioEstufa(
  form: { codigo: string; nome: string; ativo: boolean; cabines: string[]; linhas: readonly LinhaLimiteWip[] },
  modo: 'criar' | 'editar',
): ErroFormularioEstufa | null {
  if (modo === 'criar' && form.codigo.trim() === '') {
    return { campo: 'codigo', mensagem: 'Informe o código da estufa.' };
  }
  if (form.nome.trim() === '') {
    return { campo: 'nome', mensagem: 'Informe o nome da estufa.' };
  }
  if (form.ativo) {
    const semCabine = validarEstufa({ cabines: form.cabines });
    if (semCabine) return { campo: 'cabines', mensagem: semCabine };
  } else if (form.cabines.length > 0) {
    return { campo: 'cabines', mensagem: 'Desvincule as cabines antes de desativar a estufa.' };
  }
  const erroLinha = validarLinhasLimiteWip(form.linhas);
  if (erroLinha) return { campo: 'limites', indice: erroLinha.indice, mensagem: erroLinha.mensagem };
  return null;
}

// KAN-10 / D-09 / D-47: regras de uso da cabine de pintura. A ordem dos arrays e a ordem
// canonica gravada pela API (tintas: Po, Liquida, PTFE; familias: Clara, Escura, Preta, Cinza).
export const TINTAS_CABINE: readonly { value: TipoTinta; label: string }[] = [
  { value: 'PO', label: 'Pó' },
  { value: 'LIQUIDA', label: 'Líquida' },
  { value: 'PTFE', label: 'PTFE' },
];

export const FAMILIAS_COR: readonly { value: FamiliaCor; label: string }[] = [
  { value: 'CLARA', label: 'Clara' },
  { value: 'ESCURA', label: 'Escura' },
  { value: 'PRETA', label: 'Preta' },
  { value: 'CINZA', label: 'Cinza' },
];

export const PORTES_CABINE: readonly { value: PortePeca; label: string }[] = [
  { value: 'PEQUENA', label: 'Pequena' },
  { value: 'QUALQUER', label: 'Qualquer tamanho' },
];

/** String vazia representa nulo nos campos de select (servicoId, estufaId, porteMaximo). */
export interface FormularioCelulaKanban {
  servicoId: string;
  ehCabine: boolean;
  estufaId: string;
  tintasAceitas: TipoTinta[];
  familiasCorAceitas: FamiliaCor[];
  aceitaTodasCores: boolean;
  porteMaximo: PortePeca | '';
  aceitaIsolamento: boolean;
  /** Plano 17.3-11 (D-10): texto pt-BR do campo ("1,000" = sem perda de tinta). */
  fatorPerdaTinta: string;
}

export interface PayloadKanbanCelula {
  servicoId?: string | null;
  ehCabine?: boolean;
  estufaId?: string | null;
  tintasAceitas?: TipoTinta[];
  familiasCorAceitas?: FamiliaCor[];
  aceitaTodasCores?: boolean;
  porteMaximo?: PortePeca | null;
  aceitaIsolamento?: boolean;
  /** Ponto decimal (formato da API). */
  fatorPerdaTinta?: string;
}

export const MENSAGEM_FATOR_PERDA_TINTA = 'Informe um fator entre 0,001 e 9,999.';
export const FATOR_PERDA_TINTA_PADRAO = '1,000';

// Mesmo limite da API (NUMERIC(6,3), CHECK > 0): 1 inteiro e ate 3 decimais, virgula ou ponto, sem zero.
const REGEX_FATOR_PERDA = /^(?!0+(?:[.,]0+)?$)\d(?:[.,]\d{1,3})?$/;

/** Mensagem de erro do campo "Fator de perda de tinta", ou null quando o texto e valido. */
export function validarFatorPerdaTinta(texto: string): string | null {
  return REGEX_FATOR_PERDA.test(texto.trim()) ? null : MENSAGEM_FATOR_PERDA_TINTA;
}

export interface ErroRegrasCabine {
  campo: 'tintasAceitas' | 'familiasCorAceitas';
  mensagem: string;
}

const ORDEM_TINTAS: readonly string[] = TINTAS_CABINE.map((t) => t.value);
const ORDEM_FAMILIAS: readonly string[] = FAMILIAS_COR.map((f) => f.value);

function canonica<T extends string>(valores: readonly T[], ordem: readonly string[]): T[] {
  return Array.from(new Set(valores)).sort((a, b) => ordem.indexOf(a) - ordem.indexOf(b));
}

function mesmaLista(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((valor, i) => valor === b[i]);
}

export function formularioDaCelula(celula: CelulaKanbanEntity): FormularioCelulaKanban {
  return {
    servicoId: celula.servicoId ?? '',
    ehCabine: celula.ehCabine,
    estufaId: celula.estufaId ?? '',
    tintasAceitas: canonica(celula.tintasAceitas ?? [], ORDEM_TINTAS),
    familiasCorAceitas: canonica(celula.familiasCorAceitas ?? [], ORDEM_FAMILIAS),
    aceitaTodasCores: celula.aceitaTodasCores,
    porteMaximo: celula.porteMaximo ?? '',
    aceitaIsolamento: celula.aceitaIsolamento,
    fatorPerdaTinta: celula.fatorPerdaTinta ? celula.fatorPerdaTinta.replace('.', ',') : FATOR_PERDA_TINTA_PADRAO,
  };
}

/**
 * Devolve so os campos que mudaram em relacao a celula original, ou null quando nada mudou
 * (o formulario fecha sem requisicao e sem AuditEvent vazio). Com ehCabine=false o payload
 * leva so `ehCabine: false` e `estufaId: null`: as regras ficam preservadas no banco para
 * uma reativacao futura.
 */
export function montarPayloadKanbanCelula(
  original: CelulaKanbanEntity,
  form: FormularioCelulaKanban,
): PayloadKanbanCelula | null {
  const base = formularioDaCelula(original);
  const payload: PayloadKanbanCelula = {};

  if (form.servicoId !== base.servicoId) payload.servicoId = form.servicoId === '' ? null : form.servicoId;

  // O fator vale para qualquer celula (cabine ou nao), por isso entra antes do retorno antecipado de nao-cabine.
  const fator = form.fatorPerdaTinta.trim().replace(',', '.');
  if (Number(fator) !== Number(base.fatorPerdaTinta.replace(',', '.'))) payload.fatorPerdaTinta = fator;

  if (!form.ehCabine) {
    if (base.ehCabine || base.estufaId !== '') {
      payload.ehCabine = false;
      payload.estufaId = null;
    }
    return Object.keys(payload).length === 0 ? null : payload;
  }

  if (form.ehCabine !== base.ehCabine) payload.ehCabine = true;
  if (form.estufaId !== base.estufaId) payload.estufaId = form.estufaId === '' ? null : form.estufaId;

  const tintas = canonica(form.tintasAceitas, ORDEM_TINTAS);
  if (!mesmaLista(tintas, base.tintasAceitas)) payload.tintasAceitas = tintas;

  const familias = form.aceitaTodasCores ? [] : canonica(form.familiasCorAceitas, ORDEM_FAMILIAS);
  const familiasBase = base.aceitaTodasCores ? [] : base.familiasCorAceitas;
  if (form.aceitaTodasCores !== base.aceitaTodasCores) payload.aceitaTodasCores = form.aceitaTodasCores;
  if (!mesmaLista(familias, familiasBase) || (form.aceitaTodasCores && !base.aceitaTodasCores)) {
    payload.familiasCorAceitas = familias;
  }

  if (form.porteMaximo !== base.porteMaximo) payload.porteMaximo = form.porteMaximo === '' ? null : form.porteMaximo;
  if (form.aceitaIsolamento !== base.aceitaIsolamento) payload.aceitaIsolamento = form.aceitaIsolamento;

  return Object.keys(payload).length === 0 ? null : payload;
}

/**
 * WR-GAP-02: as regras da cabine so sao exigidas quando o usuario as edita (ou marca
 * "e cabine"). Assim uma cabine legada, com regras vazias, continua aceitando limites de WIP,
 * ativacao e vinculo de estufa. Mesma condicao da API (erroRegrasCabine no celula.repository).
 */
export function regrasCabineAlteradas(original: CelulaKanbanEntity, form: FormularioCelulaKanban): boolean {
  const base = formularioDaCelula(original);
  if (form.ehCabine && !base.ehCabine) return true;
  if (!mesmaLista(canonica(form.tintasAceitas, ORDEM_TINTAS), base.tintasAceitas)) return true;
  if (form.aceitaTodasCores !== base.aceitaTodasCores) return true;
  return !form.aceitaTodasCores && !mesmaLista(canonica(form.familiasCorAceitas, ORDEM_FAMILIAS), base.familiasCorAceitas);
}

/**
 * KAN-10 (empty): cabine sem tinta aceita falha TINTA para todo item que pinta e nunca e
 * sugerida; por isso nao se grava cabine sem tinta, nem sem familia quando 'Todas' esta
 * desmarcado. So valida quando ehCabine=true e, se `original` for informado, so quando as
 * regras (ou o ehCabine) mudaram em relacao a ele (WR-GAP-02).
 */
export function validarRegrasCabine(
  form: Pick<FormularioCelulaKanban, 'ehCabine' | 'tintasAceitas' | 'familiasCorAceitas' | 'aceitaTodasCores'>,
  original?: CelulaKanbanEntity,
): ErroRegrasCabine | null {
  if (!form.ehCabine) return null;
  if (original && !regrasCabineAlteradas(original, { ...formularioDaCelula(original), ...form })) return null;
  if (form.tintasAceitas.length === 0) {
    return { campo: 'tintasAceitas', mensagem: 'Selecione ao menos um tipo de tinta para a cabine.' };
  }
  if (!form.aceitaTodasCores && form.familiasCorAceitas.length === 0) {
    return { campo: 'familiasCorAceitas', mensagem: 'Selecione ao menos uma família de cor ou marque Todas.' };
  }
  return null;
}

/**
 * D-45: o cabecalho da raia do quadro aponta para /cadastros?aba=celulas&celula=CODIGO.
 * Valores aparados; vazio vira null. O codigo so e comparado com itens ja carregados.
 */
export function lerDeepLinkCadastro(search: string): { aba: string | null; celulaCodigo: string | null } {
  const params = new URLSearchParams(search);
  const limpo = (valor: string | null): string | null => {
    const texto = valor?.trim() ?? '';
    return texto === '' ? null : texto;
  };
  return { aba: limpo(params.get('aba')), celulaCodigo: limpo(params.get('celula')) };
}
